import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { MonthlyPerformance } from '@ema-atr/shared';
import { signedMoney } from './Format';
import type { CurveMode } from './PerformanceChart';

export function MonthlyPerformanceChart({ months, hasCapital }: { months: MonthlyPerformance[]; hasCapital: boolean }) {
  const years = [...new Set(months.map((month) => month.month.slice(0, 4)))].reverse();
  const [chosenYear, setChosenYear] = useState<string | null>(null);
  const year = chosenYear && years.includes(chosenYear) ? chosenYear : years[0] ?? '';
  const [mode, setMode] = useState<CurveMode>('pnl');
  const [selected, setSelected] = useState<number | null>(null);
  const [width, setWidth] = useState(800);
  const container = useRef<HTMLDivElement>(null);
  const descriptionId = useId();
  useEffect(() => {
    if (!container.current) return;
    const observer = new ResizeObserver(([entry]) => { if (entry) setWidth(Math.max(240, entry.contentRect.width)); });
    observer.observe(container.current);
    return () => observer.disconnect();
  }, []);
  const annualMonths = useMemo(() => Array.from({ length: 12 }, (_, index) => {
    const month = `${year}-${String(index + 1).padStart(2, '0')}`;
    return months.find((entry) => entry.month === month) ?? { month, realizedPnl: '0', returnRate: hasCapital ? 0 : null, closedPositions: 0 };
  }), [months, year, hasCapital]);
  const height = width < 560 ? 260 : 310;
  const left = width < 560 ? 52 : 74;
  const right = width - 16;
  const top = 20;
  const bottom = height - 38;
  const slot = (right - left) / 12;
  const values = annualMonths.map((month) => mode === 'pnl' ? Number(month.realizedPnl) : month.returnRate ?? 0);
  const lowest = Math.min(0, ...values);
  const highest = Math.max(0, ...values);
  const padding = highest === lowest ? 1 : (highest - lowest) * 0.16;
  const min = lowest - padding;
  const max = highest + padding;
  const y = (value: number) => bottom - (value - min) / (max - min) * (bottom - top);
  const x = (index: number) => left + slot * (index + 0.5);
  const active = selected === null ? null : annualMonths[selected];
  const selectAt = (clientX: number) => {
    if (!container.current) return;
    const rect = container.current.getBoundingClientRect();
    setSelected(Math.max(0, Math.min(11, Math.floor(((clientX - rect.left) / rect.width * width - left) / slot))));
  };
  const title = mode === 'pnl' ? '月度收益金额' : '月度收益率';
  return <section className="panel monthly-performance" aria-label="月度收益">
    <div className="performance-chart-heading"><div><h2>月度收益</h2><p>按平仓月份汇总 · 北京时间 · {mode === 'pnl' ? 'USDT' : '以设置本金计算，不按复利'}</p></div>
      <div className="monthly-chart-controls">
        {years.length ? <select aria-label="月度收益年份" value={year} onChange={(event) => { setChosenYear(event.target.value); setSelected(null); }}>{years.map((value) => <option key={value} value={value}>{value} 年</option>)}</select> : null}
        <div className="history-tabs" aria-label="月度图显示方式"><button aria-pressed={mode === 'pnl'} onClick={() => { setMode('pnl'); setSelected(null); }}>月度金额</button><button aria-pressed={mode === 'return'} disabled={!hasCapital} title={hasCapital ? undefined : '请先设置计算本金'} onClick={() => { setMode('return'); setSelected(null); }}>月度收益率</button></div>
      </div>
    </div>
    <div className="monthly-chart" ref={container}>
      {!months.length ? <div className="empty-state"><strong>暂无月度数据</strong><small>同步历史仓位后，按平仓月份汇总收益</small></div> : <>
        <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${year} 年${title}柱状图。可使用左右方向键查看每月明细。`} aria-describedby={descriptionId} tabIndex={0}
          onPointerMove={(event) => selectAt(event.clientX)} onPointerDown={(event) => selectAt(event.clientX)}
          onPointerLeave={() => { if (document.activeElement !== container.current?.querySelector('svg')) setSelected(null); }}
          onFocus={() => setSelected((current) => current ?? annualMonths.reduce((latest, month, index) => month.closedPositions > 0 ? index : latest, 0))} onBlur={() => setSelected(null)}
          onKeyDown={(event) => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Escape'].includes(event.key)) return;
            event.preventDefault();
            if (event.key === 'Escape') setSelected(null);
            else if (event.key === 'Home') setSelected(0);
            else if (event.key === 'End') setSelected(11);
            else setSelected(Math.max(0, Math.min(11, (selected ?? 0) + (event.key === 'ArrowLeft' ? -1 : 1))));
          }}>
          <title>{year} 年{title}，每根柱表示该月已实现净盈亏</title>
          {Array.from({ length: 5 }, (_, index) => min + (max - min) * index / 4).map((value) => <g key={value}><line x1={left} x2={right} y1={y(value)} y2={y(value)} className="chart-gridline" /><text x={left - 8} y={y(value) + 4} textAnchor="end" className="chart-axis-label">{value.toLocaleString('zh-CN', { notation: 'compact', maximumFractionDigits: 1 })}{mode === 'return' ? '%' : ''}</text></g>)}
          {selected !== null ? <rect x={left + slot * selected} y={top} width={slot} height={bottom - top} fill="var(--mint)" opacity="0.045" rx="4" /> : null}
          <line x1={left} x2={right} y1={y(0)} y2={y(0)} className="chart-zero" />
          {annualMonths.map((month, index) => {
            const value = values[index]!;
            const barWidth = Math.min(38, slot * 0.58);
            return <g key={month.month}>
              <rect className="monthly-bar" data-month={month.month} data-pnl={month.realizedPnl} x={x(index) - barWidth / 2} y={Math.min(y(0), y(value)) - (value === 0 ? 1 : 0)} width={barWidth} height={Math.max(value === 0 ? 2 : 1, Math.abs(y(value) - y(0)))} rx="3" fill={value > 0 ? 'var(--mint)' : value < 0 ? 'var(--red)' : 'var(--muted-2)'} opacity={selected === null || selected === index ? 1 : 0.6} />
              <text x={x(index)} y={height - 12} textAnchor="middle" className="chart-axis-label">{index + 1}{width < 560 ? '' : '月'}</text>
            </g>;
          })}
        </svg>
        {active && selected !== null ? <div className="monthly-tooltip" role="status" style={{ left: Math.max(8, Math.min(width - 232, x(selected) - 112)) }}>
          <span>{year} 年 {selected + 1} 月 · 已实现净盈亏</span><strong className={signedMoney(active.realizedPnl).tone}>{signedMoney(active.realizedPnl).text} <small>USDT</small></strong>
          <span>月度收益率 {active.returnRate === null ? '尚未设置本金' : `${active.returnRate > 0 ? '+' : ''}${active.returnRate.toFixed(2)}%`}</span><span>平仓 {active.closedPositions} 笔{active.closedPositions === 0 ? ' · 无平仓记录' : ''}</span>
        </div> : null}
        <p className="chart-instructions" id={descriptionId}>1—12 月 · <span className="positive">盈利</span> / <span className="negative">亏损</span> · 悬停、轻触或使用方向键查看明细</p>
      </>}
    </div>
  </section>;
}
